import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin } from 'lucide-react';
import { EnquiryForm } from './EnquiryForm';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';

const Instagram = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>;

export function Footer() {
  const [settings, setSettings] = useState<any>(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from('site_settings').select('*').single();
      if (data) setSettings(data);
    };
    fetchSettings();
  }, []);

  const whatsappLink = `https://wa.me/${settings?.whatsapp_number || '918900992020'}?text=${encodeURIComponent(
    "Hello Walnut Farm Estate, I would like to enquire about availability and booking. Please share the available dates and stay details."
  )}`;

  const footerAbout = "A meticulously designed private farmhouse retreat where modern luxury meets the timeless tranquility of nature.";

  return (
    <footer id="contact" className="bg-[#EDE7DA] pt-24 lg:pt-40 pb-12 overflow-hidden">
      <div className="max-w-screen-2xl mx-auto px-6">
        <div className="text-center mb-24 lg:mb-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <EnquiryForm />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 pb-24 border-b border-[#176B5B]/10">
          <div className="space-y-10">
            <img src={settings?.logo_url || "/assets/logo.png"} alt="Walnut Farm" className="h-16 w-auto" />
            <p className="font-clean text-sm md:text-base leading-relaxed !text-[#59635C]/70 max-w-xs">
              {footerAbout}
            </p>
            <div className="flex space-x-6">
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="opacity-40 hover:opacity-100 transition-opacity !text-[#176B5B]">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          <div className="space-y-10">
            <h4 className="font-clean font-bold text-xs uppercase tracking-widest !text-[#176B5B] opacity-60">Navigation</h4>
            <ul className="space-y-5 font-clean font-semibold uppercase tracking-widest text-[13px]">
              <li><Link to="/#home" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Home</Link></li>
              <li><Link to="/#rooms" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Rooms</Link></li>
              <li><Link to="/#amenities" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Amenities</Link></li>
              <li><Link to="/#gallery" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Gallery</Link></li>
              <li><Link to="/reviews" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Reviews</Link></li>
              <li><Link to="/store" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Store</Link></li>
              
            </ul>
          </div>

          <div className="space-y-10">
            <h4 className="font-clean font-bold text-xs uppercase tracking-widest !text-[#176B5B] opacity-60">Location</h4>
            <address className="not-italic font-clean text-sm md:text-base leading-relaxed !text-[#59635C]/70 space-y-2">
              {settings?.address ? settings.address.split(',').map((part: string, i: number) => (
                <p key={i}>{part.trim()}</p>
              )) : (
                <>
                  <p>511, Flora Farms, Pushta Road,</p>
                  <p>Sector 135, Noida,</p>
                  <p>Uttar Pradesh 201304</p>
                </>
              )}
            </address>
            <a 
              href="https://maps.google.com/?q=Walnut+Farm+Sector+135+Noida"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 font-clean font-bold text-xs uppercase tracking-widest !text-[#B8863B] hover:underline"
            >
              <MapPin className="w-3 h-3" />
              <span>Get Directions</span>
            </a>
          </div>

          <div className="space-y-10">
            <h4 className="font-clean font-bold text-xs uppercase tracking-widest !text-[#176B5B] opacity-60">Contact</h4>
            <div className="space-y-6">
              <div className="space-y-1">
                <span className="block font-clean font-bold text-[10px] uppercase tracking-widest !text-[#176B5B] opacity-60">Phone</span>
                <a href={`tel:${settings?.contact_phone || '+918900992020'}`} className="font-clean text-sm md:text-base !text-[#59635C] hover:text-[#B8863B] transition-colors">
                  {settings?.contact_phone || '+91 89009 92020'}
                </a>
              </div>
              <div className="space-y-1">
                <span className="block font-clean font-bold text-[10px] uppercase tracking-widest !text-[#176B5B] opacity-60">Email</span>
                <a href={`mailto:${settings?.contact_email || 'info@walnutfarm.in'}`} className="font-clean text-sm md:text-base !text-[#59635C] hover:text-[#B8863B] transition-colors">
                  {settings?.contact_email || 'info@walnutfarm.in'}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-12 flex flex-col md:flex-row justify-between items-center font-clean font-bold text-[10px] uppercase tracking-widest opacity-60 space-y-6 md:space-y-0">
          <span className="!text-[#176B5B]">&copy; {currentYear} {settings?.site_name || 'Walnut Farm Estate'}. All rights reserved.</span>
          <div className="flex space-x-10">
            <a href="#" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Privacy Policy</a>
            <a href="#" className="hover:!text-[#B8863B] transition-colors !text-[#176B5B]">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
